import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import MultiSelectCombo from './spray/MultiSelectCombo';
import { DataTable, Field, FormSection, Modal, Notice, StatusPill } from './patterns';
import type { DataTableColumn } from './patterns';
import { formatDateTime } from '../dateFormat';

interface AutomatedReport {
  _id?: string;
  name: string;
  type: string;
  filters: {
    farms: string[];
    cultivars: string[];
    orchards: string[];
  };
  recipients: {
    systemUsers: string[];
    manualEmails: string[];
  };
  schedule: {
    frequency: 'daily' | 'weekly' | 'monthly';
    day?: number;
    time: string;
  };
  status: 'active' | 'inactive';
  lastRun?: string;
}

const ScheduledReportManager: React.FC = () => {
  const { t } = useTranslation();
  const [reports, setReports] = useState<AutomatedReport[]>([]);
  const [orchards, setOrchards] = useState<Array<{ _id: string; orchardName: string; farmName?: string; cultivar?: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReport, setEditingReport] = useState<AutomatedReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const initialReport: AutomatedReport = {
    name: '',
    type: 'scouting',
    filters: { farms: [], cultivars: [], orchards: [] },
    recipients: {
      systemUsers: [],
      manualEmails: []
    },
    schedule: {
      frequency: 'daily',
      time: '08:00'
    },
    status: 'active'
  };

  const [formData, setFormData] = useState<AutomatedReport>(initialReport);
  const [manualEmailsStr, setManualEmailsStr] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || 'citrus-synch-mock-token-123';
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [reportsRes, orchardsRes] = await Promise.all([
        axios.get('/api/v1/reports/automated', config),
        axios.get('/api/v1/orchards', config),
      ]);

      setReports(reportsRes.data);
      setOrchards(orchardsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch scheduled reports:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleShowModal = (report: AutomatedReport | null = null) => {
    if (report) {
      setEditingReport(report);
      setFormData(report);
      setManualEmailsStr(report.recipients.manualEmails.join(', '));
    } else {
      setEditingReport(null);
      setFormData(initialReport);
      setManualEmailsStr('');
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.schedule.time) {
      setError('Please fill in all required fields.');
      return;
    }

    const reportToSave = {
      ...formData,
      recipients: {
        ...formData.recipients,
        manualEmails: manualEmailsStr.split(',').map(e => e.trim()).filter(e => e !== '')
      }
    };

    try {
      const token = localStorage.getItem('token') || 'citrus-synch-mock-token-123';
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingReport?._id) {
        await axios.put(`/api/v1/reports/automated/${editingReport._id}`, reportToSave, config);
      } else {
        await axios.post('/api/v1/reports/automated', reportToSave, config);
      }

      handleCloseModal();
      fetchData();
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.error || 'Failed to save report.');
      } else {
        setError('An unexpected error occurred while saving the report.');
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('scheduledReports.deleteConfirm'))) return;

    try {
      const token = localStorage.getItem('token') || 'citrus-synch-mock-token-123';
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`/api/v1/reports/automated/${id}`, config);
      fetchData();
    } catch (err) {
      setError('Failed to delete report.');
    }
  };

  const toggleStatus = async (report: AutomatedReport) => {
    try {
      const token = localStorage.getItem('token') || 'citrus-synch-mock-token-123';
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const newStatus = report.status === 'active' ? 'inactive' : 'active';
      await axios.put(`/api/v1/reports/automated/${report._id}`, { status: newStatus }, config);
      fetchData();
    } catch (err) {
      setError('Failed to update status.');
    }
  };

  if (loading && reports.length === 0) {
    return (
      <div className="scheduled-report-manager__loading">
        {t('scheduledReports.heading')}…
      </div>
    );
  }

  const columns: DataTableColumn<AutomatedReport>[] = [
    {
      key: 'name',
      label: t('scheduledReports.nameHeader'),
      render: (report) => report.name,
    },
    {
      key: 'type',
      label: t('scheduledReports.typeHeader'),
      render: (report) => <StatusPill variant="info">{report.type}</StatusPill>,
    },
    {
      key: 'schedule',
      label: t('scheduledReports.scheduleHeader'),
      render: (report) => (
        <>
          {report.schedule.frequency.charAt(0).toUpperCase() + report.schedule.frequency.slice(1)}
          {report.schedule.frequency === 'weekly' && report.schedule.day !== undefined && ` (Day ${report.schedule.day})`}
          {report.schedule.frequency === 'monthly' && report.schedule.day !== undefined && ` (Day ${report.schedule.day})`}
          {` at ${report.schedule.time}`}
        </>
      ),
    },
    {
      key: 'status',
      label: t('scheduledReports.statusHeader'),
      render: (report) => (
        <button
          type="button"
          className="scheduled-report-manager__status-button"
          onClick={() => toggleStatus(report)}
        >
          <StatusPill variant={report.status === 'active' ? 'ok' : 'info'}>
            {report.status.toUpperCase()}
          </StatusPill>
        </button>
      ),
    },
    {
      key: 'lastRun',
      label: t('scheduledReports.lastRunHeader'),
      render: (report) =>
        report.lastRun ? formatDateTime(report.lastRun) : t('scheduledReports.neverRun'),
    },
    {
      key: 'actions',
      label: t('scheduledReports.actionsHeader'),
      render: (report) => (
        <div className="scheduled-report-manager__row-actions">
          <button
            type="button"
            className="pat-btn pat-btn--outline"
            onClick={() => handleShowModal(report)}
          >
            {t('scheduledReports.edit')}
          </button>
          <button
            type="button"
            className="pat-btn pat-btn--danger"
            onClick={() => handleDelete(report._id!)}
          >
            {t('scheduledReports.delete')}
          </button>
        </div>
      ),
    },
  ];

  const orchardOptions = orchards.map(o => ({ code: o._id, label: o.orchardName }));
  const farmOptions = Array.from(new Set(orchards.map(o => o.farmName).filter(Boolean) as string[]))
    .sort((a, b) => a.localeCompare(b))
    .map(f => ({ code: f, label: f }));
  const cultivarOptions = Array.from(new Set(orchards.map(o => o.cultivar).filter(Boolean) as string[]))
    .sort((a, b) => a.localeCompare(b))
    .map(c => ({ code: c, label: c }));

  return (
    <div className="scheduled-report-manager">
      <div className="scheduled-report-manager__header">
        <h3 className="scheduled-report-manager__heading">{t('scheduledReports.heading')}</h3>
        <button
          type="button"
          className="pat-btn pat-btn--primary"
          onClick={() => handleShowModal()}
        >
          <i className="bi bi-plus-lg" aria-hidden="true" /> {t('scheduledReports.newReport')}
        </button>
      </div>

      {error && (
        <Notice tone="danger" action={<button type="button" className="scheduled-report-manager__alert-close" aria-label="Dismiss" onClick={() => setError(null)}>×</button>}>
          <span>{error}</span>
        </Notice>
      )}

      {reports.length === 0 ? (
        <Notice tone="info">
          {t('scheduledReports.noReports')}
        </Notice>
      ) : (
        <DataTable<AutomatedReport>
          columns={columns}
          rows={reports}
          rowKey={(report) => report._id || report.name}
        />
      )}

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        title={editingReport ? t('scheduledReports.editTitle') : t('scheduledReports.newTitle')}
        size="lg"
      >
        <Modal.Body>
          <FormSection id="report-meta" title={t('scheduledReports.editTitle')}>
            <div className="scheduled-report-manager__row">
              <Field label={t('scheduledReports.reportName')} controlId="reportName">
                <input
                  id="reportName"
                  type="text"
                  placeholder={t('scheduledReports.reportNamePlaceholder')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Field>
              <Field label={t('scheduledReports.orchards')} controlId="reportOrchards">
                <MultiSelectCombo
                  items={orchardOptions}
                  value={formData.filters.orchards}
                  onChange={(codes) => setFormData({ ...formData, filters: { ...formData.filters, orchards: codes } })}
                  ariaLabel={t('scheduledReports.orchards')}
                  placeholder={t('scheduledReports.scopeAllHint')}
                />
              </Field>
            </div>

            <div className="scheduled-report-manager__row">
              <Field label={t('scheduledReports.farms')} controlId="reportFarms">
                <MultiSelectCombo
                  items={farmOptions}
                  value={formData.filters.farms}
                  onChange={(codes) => setFormData({ ...formData, filters: { ...formData.filters, farms: codes } })}
                  ariaLabel={t('scheduledReports.farms')}
                  placeholder={t('scheduledReports.scopeAllHint')}
                />
              </Field>
              <Field label={t('scheduledReports.cultivars')} controlId="reportCultivars">
                <MultiSelectCombo
                  items={cultivarOptions}
                  value={formData.filters.cultivars}
                  onChange={(codes) => setFormData({ ...formData, filters: { ...formData.filters, cultivars: codes } })}
                  ariaLabel={t('scheduledReports.cultivars')}
                  placeholder={t('scheduledReports.scopeAllHint')}
                />
              </Field>
            </div>

            <div className="scheduled-report-manager__row">
              <Field label={t('scheduledReports.frequency')} controlId="scheduleFreq">
                <select
                  id="scheduleFreq"
                  value={formData.schedule.frequency}
                  onChange={(e) => setFormData({
                    ...formData,
                    schedule: {
                      ...formData.schedule,
                      frequency: e.target.value as any,
                      day: e.target.value === 'daily' ? undefined : (formData.schedule.day || 1)
                    }
                  })}
                >
                  <option value="daily">{t('scheduledReports.daily')}</option>
                  <option value="weekly">{t('scheduledReports.weekly')}</option>
                  <option value="monthly">{t('scheduledReports.monthly')}</option>
                </select>
              </Field>

              {formData.schedule.frequency !== 'daily' && (
                <Field
                  label={formData.schedule.frequency === 'weekly' ? t('scheduledReports.dayOfWeek') : t('scheduledReports.dayOfMonth')}
                  controlId="scheduleDay"
                >
                  <select
                    id="scheduleDay"
                    value={formData.schedule.day}
                    onChange={(e) => setFormData({
                      ...formData,
                      schedule: { ...formData.schedule, day: parseInt(e.target.value) }
                    })}
                  >
                    {formData.schedule.frequency === 'weekly' ? (
                      <>
                        <option value="0">{t('scheduledReports.sunday')}</option>
                        <option value="1">{t('scheduledReports.monday')}</option>
                        <option value="2">{t('scheduledReports.tuesday')}</option>
                        <option value="3">{t('scheduledReports.wednesday')}</option>
                        <option value="4">{t('scheduledReports.thursday')}</option>
                        <option value="5">{t('scheduledReports.friday')}</option>
                        <option value="6">{t('scheduledReports.saturday')}</option>
                      </>
                    ) : (
                      Array.from({ length: 31 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))
                    )}
                  </select>
                </Field>
              )}

              <Field label={t('scheduledReports.time')} controlId="scheduleTime">
                <input
                  id="scheduleTime"
                  type="time"
                  value={formData.schedule.time}
                  onChange={(e) => setFormData({
                    ...formData,
                    schedule: { ...formData.schedule, time: e.target.value }
                  })}
                />
              </Field>
            </div>

            <Field
              label={t('scheduledReports.recipients')}
              controlId="manualEmails"
              helpText={t('scheduledReports.recipientsHelp')}
            >
              <textarea
                id="manualEmails"
                rows={2}
                placeholder={t('scheduledReports.recipientsPlaceholder')}
                value={manualEmailsStr}
                onChange={(e) => setManualEmailsStr(e.target.value)}
              />
            </Field>

            <label className="scheduled-report-manager__switch-row">
              <input
                type="checkbox"
                id="status-switch"
                checked={formData.status === 'active'}
                onChange={(e) => setFormData({ ...formData, status: e.target.checked ? 'active' : 'inactive' })}
              />
              <span>{t('scheduledReports.active')}</span>
            </label>
          </FormSection>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            onClick={handleCloseModal}
          >
            {t('scheduledReports.cancel')}
          </button>
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            onClick={handleSave}
          >
            {t('scheduledReports.saveReport')}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ScheduledReportManager;
